import React, { useState, useEffect } from 'react'
import { useBlocker } from 'react-router'
import { Box, Stack, Alert, Button, SimpleTabs, UnsavedModal } from '@/components/lib'
import useStore from '../../../store'
import useTitle from '../../../hooks/useTitle'
import api from '../../../api'
import utils from '../../../utils/utils'

import Overview from './Overview'
import Code from './Code'
import Sponsor from './Sponsor'
import Name from './Name'
import Terms from './Terms'
import Approval from './Approval'

const FIELDS = [
  { section: 'code', label: 'Trial code', type: 'text', key: 'code', required: true, placeholder: 'ACMETrial1' },
  { section: 'sponsor', label: 'Trial sponsor', type: 'text', key: 'sponsor', placeholder: 'ACME Inc.' },
  { section: 'identity', label: 'Trial name (public title)', type: 'text', key: 'name', required: true, placeholder: 'ACME Sleep Quality Trial' },
  { section: 'identity', label: 'Scientific title', type: 'text', key: 'scientificName' },
  { section: 'identity', label: 'Trial description', type: 'textarea', key: 'description', required: true, placeholder: 'This trial is designed to measure the quality of sleep in participants over a 30-day period...' },
  { section: 'identity', label: 'Study acronym', type: 'text', key: 'studyAcronym', placeholder: 'ACMESQT' },
  { section: 'identity', label: 'EudraCT number', type: 'text', key: 'eudractNumber' },
  { section: 'identity', label: 'CTIS number', type: 'text', key: 'ctisNumber' },
  { section: 'identity', label: 'ClinicalTrials.gov number', type: 'text', key: 'clinicalTrialsGovNumber', placeholder: 'NCT012345' },
  { section: 'identity', label: 'Protocol or serial number', type: 'text', key: 'protocolNumber' }
]

export default function Info () {
  const [selectedTabKey, setSelectedTabKey] = useState('overview')
  const [data, setData] = useState({})
  const [saving, setSaving] = useState(false)
  const [unsaved, setUnsaved] = useState(false)
  const [error, setError] = useState()
  const { user, trial, updateTrial } = useStore()
  useTitle('About', trial)

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    unsaved &&
    currentLocation.pathname !== nextLocation.pathname
  )

  useEffect(() => {
    setData({
      code: trial.code,
      name: trial.name,
      sponsor: trial.sponsor,
      scientificName: trial.scientificName,
      description: trial.description,
      studyAcronym: trial.studyAcronym,
      eudractNumber: trial.eudractNumber,
      ctisNumber: trial.ctisNumber,
      clinicalTrialsGovNumber: trial.clinicalTrialsGovNumber,
      protocolNumber: trial.protocolNumber,
      conditions: trial.conditions,
      approvalNeeded: trial.approvalNeeded || false,
      approvals: trial.approvals || []
    })
  }, [trial])

  function updateData (newData) {
    setData({ ...data, ...newData })
    setUnsaved(true)
  }

  function save () {
    if (!data.code || !data.name) {
      return setError('Code and name are required fields.')
    }
    setSaving(true)
    setError()
    api.trials.update(trial._id, data, t => {
      updateTrial(t._id, t)
      utils.toast('success', 'Information saved', 'Your trial details have been updated.')
      setSaving(false)
      setUnsaved(false)
    }, err => {
      setSaving(false)
      setError(err.message)
    })
  }

  const TABS = [
    {
      title: 'Overview',
      key: 'overview',
      content: <Overview fields={FIELDS} trial={trial} data={data} setTab={key => setSelectedTabKey(TABS.find(t => t.key === key)?.key)} />
    }
  ]
  if (utils.canManageTrial(user, trial)) {
    TABS.push(...[
      {
        title: 'Code',
        key: 'code',
        content: <Code fields={FIELDS.filter(f => f.section === 'code')} trial={trial} data={data} updateData={updateData} />
      },
      {
        title: 'Sponsor',
        key: 'sponsor',
        content: <Sponsor fields={FIELDS.filter(f => f.section === 'sponsor')} trial={trial} data={data} updateData={updateData} />
      },
      {
        title: 'Identity',
        key: 'identity',
        content: <Name fields={FIELDS.filter(f => f.section === 'identity')} trial={trial} data={data} updateData={updateData} />
      },
      {
        title: 'Privacy',
        key: 'privacy',
        content: <Terms trial={trial} data={data} updateData={updateData} />
      },
      {
        title: 'Approval',
        key: 'approval',
        content: <Approval trial={trial} data={data} updateData={updateData} />
      }
    ])
  }

  return (
    <Stack gap={10} position='relative'>
      <UnsavedModal blocker={blocker} />
      <SimpleTabs
        lazyMount
        value={selectedTabKey}
        onValueChange={setSelectedTabKey}
        tabs={TABS}
      />

      {utils.canManageTrial(user, trial) &&
        <Box mx={10} position='sticky' style={{ bottom: '15px' }} boxShadow='lg' p={3} rounded='md' bg='bg.surface'>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Box>
              {error
                ? (
                  <Alert status='error' title='There was a problem saving your trial details'>
                    {error} -  Please fix these issues and try again.
                  </Alert>)
                : <Box />}
              {unsaved
                ? (
                  <Alert status='info' title='You have unsaved changes'>
                    Remember to save before leaving.
                  </Alert>)
                : <Box />}
            </Box>
            <Button colorPalette='primary' onClick={save} isLoading={saving}>Save changes</Button>
          </Stack>
        </Box>}
    </Stack>
  )
}
